import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { monthlyEquivalent, normalizePayFrequency, type TierRate } from "@torre/team";
import ApiCrudSection, {
  type Column,
  type FormField,
  type Row,
} from "../components/ApiCrudSection";
import {
  CURRENCY_OPTIONS,
  MODE_OPTIONS,
  modeLabel,
  MODE_TONES,
  PAY_FREQUENCY_OPTIONS,
  payFrequencyLabel,
  PRICING_TYPE_OPTIONS,
  pricingTypeLabel,
  ROLE_OPTIONS,
  roleLabel,
  ROLE_TONES,
  formatMoney,
} from "../theme";
import {
  fetchPayRates,
  createPayRate,
  updatePayRate,
  deletePayRate,
} from "../services/teamService";
import type { PayRate } from "../types";
import { useIsStaffAdmin } from "../../../hooks/useIsStaffAdmin";

// Catálogo de tarifas de pago (AgroCloud#13): modalidad por día o por entregable, y para
// entregables precio único o precio por rangos de entregables, más el bono por
// entregables. Es la fuente de la que el cálculo del pago toma sus precios.
export default function RatesSection() {
  // #1007: solo quien administra modifica; los demás ven lo suyo en solo lectura.
  const isStaffAdmin = useIsStaffAdmin();
  const { t } = useTranslation("common");
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { items } = await fetchPayRates();
      setRows(items as unknown as Row[]);
    } catch {
      setError(t("team.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Resume el precio de la tarifa en una celda según su modalidad. */
  const priceSummary = (r: Row): string => {
    // Los importes viajan TAL CUAL: `Number(null)` daría 0 y una tarifa sin
    // precio se pintaría "MXN 0" (regla del módulo: sin dato NO se paga cero).
    if (r.mode === "daily") return `${formatMoney(r.dailyRate)} / día`;
    if (r.mode === "hourly") return `${formatMoney(r.hourlyRate)} / h`;
    if (r.mode === "project") return formatMoney(r.agreedAmount);
    if (r.mode === "fixed_salary") {
      // #1296: un rango de sueldo dice cuánto, en qué moneda y cada cuánto; si
      // no es mensual se acompaña del equivalente al mes, que es como la
      // empresa habla de los sueldos. La moneda de la tarifa existe siempre
      // (el servidor pone MXN si no se declara).
      const frequency = normalizePayFrequency(r.payFrequency);
      const currency = String(r.currency || "MXN");
      // La periodicidad ya está normalizada: la clave existe siempre, igual que
      // en la columna Sueldo del padrón.
      const base = `${formatMoney(r.fixedSalary, currency)} ${t(`team.salary.per.${frequency}`)}`;
      const amount = Number(r.fixedSalary);
      if (frequency === "monthly" || !Number.isFinite(amount) || amount <= 0 || r.fixedSalary === null) return base;
      return `${base} · ${t("team.salary.monthly", { amount: formatMoney(monthlyEquivalent(amount, frequency), currency) })}`;
    }
    if (r.pricingType === "tiered") {
      const tiers = (r.tiers as TierRate[] | undefined) ?? [];
      if (tiers.length === 0) return "—";
      const prices = tiers.map((x) => Number(x.price));
      return `${formatMoney(Math.min(...prices))} – ${formatMoney(Math.max(...prices))}`;
    }
    return formatMoney(r.unitPrice);
  };

  const columns: Column[] = [
    { key: "name", label: t("team.fields.work") },
    { key: "code", label: t("team.fields.code") },
    {
      // #1364: el puesto al que paga la tarifa (dev, tech lead, diseño, ventas…).
      key: "position",
      label: t("team.fields.position"),
      render: (r) =>
        r.position ? (
          <span
            data-testid="rate-position"
            className={`rounded-full px-2 py-0.5 text-[11px] ${ROLE_TONES[String(r.position)] ?? "bg-gray-400/15 text-gray-500"}`}
          >
            {roleLabel(t, r.position)}
          </span>
        ) : (
          "—"
        ),
    },
    {
      key: "mode",
      label: t("team.fields.mode"),
      render: (r) => (
        <span
          className={`rounded-full px-2 py-0.5 text-[11px] ${
            MODE_TONES[String(r.mode)] ?? "bg-gray-400/15 text-gray-500"
          }`}
        >
          {modeLabel(t, r.mode)}
        </span>
      ),
    },
    {
      key: "pricingType",
      label: t("team.fields.pricingType"),
      render: (r) =>
        r.mode !== "deliverable"
          ? "—"
          : pricingTypeLabel(t, r.pricingType),
    },
    { key: "outputUnit", label: t("team.fields.unit") },
    {
      key: "price",
      label: t("team.fields.price"),
      align: "right",
      render: priceSummary,
    },
    {
      key: "bonusAmount",
      label: t("team.fields.bonus"),
      align: "right",
      render: (r) =>
        r.bonusAmount
          ? `${formatMoney(r.bonusAmount)} @ ${String(r.bonusMinOutput ?? "—")}`
          : "—",
    },
    {
      key: "active",
      label: t("team.fields.active"),
      align: "center",
      render: (r) => (
        <span
          className={`rounded-full px-2 py-0.5 text-[11px] ${
            r.active === false
              ? "bg-gray-400/15 text-gray-500"
              : "bg-emerald-500/15 text-emerald-500"
          }`}
        >
          {r.active === false ? t("team.no") : t("team.si")}
        </span>
      ),
    },
  ];

  const isMode = (...modes: string[]) => (v: Record<string, string>) =>
    modes.includes(v.mode || "deliverable");

  const fields: FormField[] = [
    { key: "name", label: t("team.fields.work"), required: true },
    { key: "code", label: t("team.fields.code") },
    {
      key: "mode",
      label: t("team.fields.mode"),
      type: "select",
      required: true,
      defaultValue: "deliverable",
      options: MODE_OPTIONS.map((m) => ({ value: m, label: modeLabel(t, m) })),
      hint: t("team.hints.mode"),
    },
    {
      // #1296: antes la moneda sólo la ponía el servidor (MXN) y un rango en
      // dólares se guardaba como pesos sin que nadie lo viera.
      key: "currency",
      label: t("team.fields.currency"),
      type: "select",
      defaultValue: "MXN",
      options: CURRENCY_OPTIONS.map((c) => ({ value: c, label: c })),
      hint: t("team.hints.rateCurrency"),
    },
    {
      key: "pricingType",
      label: t("team.fields.pricingType"),
      type: "select",
      defaultValue: "flat",
      options: PRICING_TYPE_OPTIONS.map((x) => ({
        value: x,
        label: pricingTypeLabel(t, x),
      })),
      hint: t("team.hints.pricingType"),
      visibleWhen: isMode("deliverable"),
    },
    {
      key: "outputUnit",
      label: t("team.fields.unit"),
      hint: t("team.hints.unit"),
      visibleWhen: isMode("deliverable", "hourly"),
    },
    {
      key: "unitPrice",
      label: t("team.fields.unitPrice"),
      type: "number",
      hint: t("team.hints.unitPrice"),
      visibleWhen: (v) => isMode("deliverable")(v) && (v.pricingType || "flat") === "flat",
    },
    {
      key: "hourlyRate",
      label: t("team.fields.hourlyRate"),
      type: "number",
      hint: t("team.hints.hourlyRate"),
      visibleWhen: isMode("hourly"),
    },
    {
      key: "dailyRate",
      label: t("team.fields.dailyRate"),
      type: "number",
      hint: t("team.hints.rateDailyRate"),
      visibleWhen: isMode("daily"),
    },
    {
      key: "agreedAmount",
      label: t("team.fields.agreedAmount"),
      type: "number",
      hint: t("team.hints.agreedAmount"),
      visibleWhen: isMode("project"),
    },
    {
      // #1364: un rango de sueldo es de un puesto; la ficha del colaborador
      // toma el puesto de su rango.
      key: "position",
      label: t("team.fields.position"),
      type: "select",
      options: ROLE_OPTIONS.map((c) => ({ value: c, label: roleLabel(t, c) })),
      hint: t("team.hints.ratePosition"),
      visibleWhen: isMode("fixed_salary"),
    },
    {
      key: "fixedSalary",
      label: t("team.fields.fixedSalary"),
      type: "number",
      hint: t("team.hints.fixedSalary"),
      visibleWhen: isMode("fixed_salary"),
    },
    {
      // #1296: cada cuánto se paga el sueldo del rango («al mes», «por quincena»).
      key: "payFrequency",
      label: t("team.fields.payFrequency"),
      type: "select",
      defaultValue: "semimonthly",
      options: PAY_FREQUENCY_OPTIONS.map((f) => ({ value: f, label: payFrequencyLabel(t, f) })),
      hint: t("team.hints.ratePayFrequency"),
      visibleWhen: isMode("fixed_salary"),
    },
    {
      key: "tiers",
      visibleWhen: (v) => isMode("deliverable")(v) && v.pricingType === "tiered",
      label: t("team.fields.tiers"),
      type: "repeat",
      addRowLabel: t("team.addTier"),
      repeatColumns: [
        { key: "from", label: t("team.fields.from"), type: "number" },
        { key: "to", label: t("team.fields.to"), type: "number" },
        { key: "price", label: t("team.fields.price"), type: "number" },
      ],
    },
    {
      key: "bonusMinOutput",
      label: t("team.fields.bonusMin"),
      type: "number",
      hint: t("team.hints.bonusMin"),
      visibleWhen: isMode("deliverable", "hourly", "daily"),
    },
    {
      key: "bonusAmount",
      label: t("team.fields.bonusAmount"),
      type: "number",
      visibleWhen: isMode("deliverable", "hourly", "daily"),
    },
    {
      key: "costCenterId",
      label: t("team.fields.costCenter"),
      hint: t("team.hints.costCenter"),
    },
    { key: "notes", label: t("team.fields.notes"), type: "textarea" },
  ];

  const toPayload = (values: Record<string, string>): Partial<PayRate> => {
    let tiers: TierRate[] = [];
    try {
      const parsed = JSON.parse(values.tiers || "[]");
      if (Array.isArray(parsed)) {
        tiers = (parsed as TierRate[]).map((x) => ({
          from: Number(x.from) || 0,
          // Un `to` vacío/0 significa tramo abierto (sin tope superior).
          to: x.to === null || x.to === undefined || Number(x.to) === 0
            ? null
            : Number(x.to),
          price: Number(x.price) || 0,
        }));
      }
    } catch {
      tiers = [];
    }
    const mode = (values.mode || "deliverable") as PayRate["mode"];
    return {
      name: values.name,
      code: values.code || null,
      mode,
      pricingType: mode !== "deliverable"
        ? undefined
        : ((values.pricingType || "flat") as PayRate["pricingType"]),
      outputUnit: values.outputUnit || null,
      unitPrice: values.unitPrice ? Number(values.unitPrice) : null,
      dailyRate: values.dailyRate ? Number(values.dailyRate) : null,
      hourlyRate: values.hourlyRate ? Number(values.hourlyRate) : null,
      agreedAmount: values.agreedAmount ? Number(values.agreedAmount) : null,
      fixedSalary: values.fixedSalary ? Number(values.fixedSalary) : null,
      currency: values.currency || "MXN",
      // La periodicidad y el puesto sólo significan algo en un sueldo fijo.
      payFrequency: mode === "fixed_salary" ? normalizePayFrequency(values.payFrequency) : null,
      position: mode === "fixed_salary" && values.position ? (values.position as PayRate["position"]) : null,
      tiers,
      bonusMinOutput: values.bonusMinOutput
        ? Number(values.bonusMinOutput)
        : null,
      bonusAmount: values.bonusAmount ? Number(values.bonusAmount) : null,
      costCenterId: values.costCenterId || null,
      notes: values.notes || null,
    };
  };

  return (
    <ApiCrudSection
      readOnly={!isStaffAdmin}
      title={t("team.menu.rates")}
      subtitle={t("team.ratesSubtitle")}
      icon="ri-price-tag-3-line"
      columns={columns}
      fields={fields}
      rows={rows}
      loading={loading}
      error={error}
      searchKey="name"
      addLabel={t("team.newRate")}
      onCreate={async (values) => {
        await createPayRate(toPayload(values));
        await load();
      }}
      onUpdate={async (id, values) => {
        await updatePayRate(id, toPayload(values));
        await load();
      }}
      onDelete={async (id) => {
        await deletePayRate(id);
        await load();
      }}
    />
  );
}
